import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  QueryList
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { CheckboxComponent } from './checkbox.component';

@Component({
  selector: 'axis-checkbox-wrapper',
  template: '<ng-content></ng-content>',
  host: {
    class: 'axis-checkbox-wrapper',
    role: 'group'
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxWrapperComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CheckboxWrapperComponent),
      multi: true
    }
  ]
})
export class CheckboxWrapperComponent implements AfterContentInit, OnDestroy {
  public isValid = false;
  public model = new Array<any>();
  @Input() isAllChecked = false;
  @Input() isAllDisabled = false;
  @Input() rel: string;
  @Input() required = false;

  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @ContentChildren(CheckboxComponent)
  checkboxComponents: QueryList<CheckboxComponent>;

  private propagateModelChange = (_: any) => { };
  private OnTouchedEvent = () => { };

  ngAfterContentInit() {
    this.checkboxComponents
      .toArray()
      .forEach((comp: CheckboxComponent, index) => {
        comp.changeDetect.subscribe(() => {
          if (comp.checked) {
            this.model.push({
              name: comp.name || '',
              value: comp.value,
              index
            });
          } else {
            for (let i = 0; i < this.model.length; i++) {
              if (this.model[i].index === index) {
                this.model.splice(i, 1);
              }
            }
          }
          this.propagateModelChange(this.model);
          this.onSelect.emit(this.model);
        });
        comp.emitBlurEvent.subscribe(() => {
          this.OnTouchedEvent();
        });
        if (comp.checked) {
          this.updateModel(comp, index);
        }
      });

    if (this.isAllChecked) {
      this.selectAll();
    }
    if (this.isAllDisabled) {
      this.toggleDisable();
    }
    if (this.model) {
      this.writeValue(this.model);
    }
  }

  // Implementing ControlValueAccessor for RadioButtonWrapperComponent
  // ::For custom form validations(triggered when assigned to ngModel)
  writeValue(value: any) {
    if (value || value === 0) {
      this.model = value;
      this.preDefinedSelection(this.model);
    } else {
      this.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.propagateModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.OnTouchedEvent = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (this.checkboxComponents && this.checkboxComponents.length) {
      this.checkboxComponents.toArray().forEach((comp: CheckboxComponent) => {
        comp.disabled = isDisabled;
      });
    }
  }

  preDefinedSelection(model) {
    const modellength = model.length;
    if (this.checkboxComponents) {
      this.model = new Array<any>();
      this.checkboxComponents
        .toArray()
        .forEach((comp: CheckboxComponent, index) => {
          const isMatched = false;

          if (typeof model === 'string') {
            if (model === comp.value) {
              this.updateModel(comp, index);
            }
          } else if (typeof model === 'number') {
            if (model === index) {
              this.updateModel(comp, index);
            }
          } else if (Array.isArray(model)) {
            if (model && modellength > 0) {
              for (let i = 0; i < modellength; i++) {
                if (typeof model[i] === 'string') {
                  if (model[i] === comp.value) {
                    this.updateModel(comp, index);
                  }
                } else if (typeof model[i] === 'number') {
                  if (model[i] === index) {
                    this.updateModel(comp, index);
                  }
                } else {
                  // if (model[i].value === comp.value) {
                  //   this.updateModel(comp, index);
                  // }
                  const checkboxVal = {
                    name: comp.name,
                    value: comp.value
                  };
                  if (this.compareObjects(model[i], checkboxVal)) {
                    this.updateModel(comp, index);
                  }
                }
              }
            }
          } else {
            // if (model.value === comp.value) {
            //   this.updateModel(comp, index);
            // }
            const checkboxVal = {
              name: comp.name,
              value: comp.value
            };
            if (this.compareObjects(model, checkboxVal)) {
              this.updateModel(comp, index);
            }
          }
        });
    }
  }

  compareObjects(obj1, obj2): boolean {
    const comparator1 = Object.assign({}, obj1);
    const comparator2 = Object.assign({}, obj2);
    if (comparator1.hasOwnProperty('index')) {
      delete comparator1.index;
    }
    if (comparator2.hasOwnProperty('index')) {
      delete comparator2.index;
    }
    return this.isEquivalent(comparator1, comparator2);
  }
  isEquivalent(obj1, obj2) {
    const obj1Props = Object.getOwnPropertyNames(obj1);
    const obj2Props = Object.getOwnPropertyNames(obj2);
    if (obj1Props.length !== obj2Props.length) {
      return false;
    }
    for (let i = 0; i < obj1Props.length; i++) {
      const propName = obj1Props[i];
      if (obj1[propName] !== obj2[propName]) {
        return false;
      }
    }
    return true;
  }
  updateModel(comp: CheckboxComponent, index) {
    comp.checked = true;
    this.model.push({ name: comp.name || '', value: comp.value, index });
    if (!comp._changeDetRef['destroyed']) {
      comp._changeDetRef.detectChanges();
    }
  }

  toggleDisable() {
    this.checkboxComponents.toArray().forEach((comp: CheckboxComponent) => {
      comp.disabled = !comp.disabled;
      if (!comp._changeDetRef['destroyed']) {
        comp._changeDetRef.detectChanges();
      }
    });
  }

  toggleAllCheck() {
    this.model = new Array<any>();
    this.checkboxComponents
      .toArray()
      .forEach((comp: CheckboxComponent, index) => {
        comp.checked = !comp.checked;
        if (comp.checked) {
          const obj = {
            name: comp.name || '',
            value: comp.value,
            index
          };
          this.model.push(obj);
        }
      });
    this.propagateModelChange(this.model);
    this.onSelect.emit(this.model);
  }

  selectAll() {
    this.model = new Array<any>();
    this.checkboxComponents
      .toArray()
      .forEach((comp: CheckboxComponent, index) => {
        comp.checked = true;
        const obj = { name: comp.name || '', value: comp.value, index };
        this.model.push(obj);
        if (!comp._changeDetRef['destroyed']) {
          comp._changeDetRef.detectChanges();
        }
      });
    this.propagateModelChange(this.model);
    this.onSelect.emit(this.model);
  }

  deSelectAll() {
    this.checkboxComponents.toArray().forEach((comp: CheckboxComponent) => {
      comp.checked = false;
    });
    this.model = new Array<any>();
    this.propagateModelChange(this.model);
    this.onSelect.emit(this.model);
  }

  validate(control: AbstractControl) {
    if (this.required) {
      if (control.value !== -1) {
        return null;
      }
      return { axisCheckbox: { valid: false } };
    }
  }

  reset() {
    if (this.checkboxComponents) {
      this.checkboxComponents.toArray().forEach((comp: CheckboxComponent) => {
        comp.checked = false;
      });
    }
    this.model = new Array<any>();
  }

  ngOnDestroy() {
    this.checkboxComponents.toArray().forEach((comp: CheckboxComponent) => {
      comp.changeDetect.unsubscribe();
      comp.emitBlurEvent.unsubscribe();
    });
  }
}
